'use client'

import * as React from 'react'
import type { InstantSearchServerState } from 'react-instantsearch'
import {
  Configure,
  useHits,
  useInstantSearch,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch'
import type { SearchProduct } from 'cf-menu/components/search/search.types'
import { ProductCard, ProductCardSkeleton } from '@web/components/product-card/product-card'
import { useLocale, useTranslations } from 'next-intl'
import { GoogleItemListJson } from '@repo/analytics/google/item-list-json'
import { Header } from './header'
import type { Locales } from 'cf-i18n/helpers'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { algoliaProductIndex, defaultSearchClient } from 'algolia/algolia'
import { AppRoute } from 'cf-menu/utils/route'
import { Links } from './links/links'
import { SearchResultsSort } from '@web/app/[locale]/(web)/search/_components/results/search-results-sort'
import { Typography } from '@repo/design-system-cf/components/typography'
import { IconArrowForward } from '@repo/design-system-cf/components/icons'
import { Link as LinkComponent } from '@repo/design-system-cf/components/link'
import { Pagination } from '@web/components/pagination'
import { HITS_PER_PAGE } from './get-initial-search-state'
import { FreeTrialBannerSection } from '@web/components/free-trial-banner/loader'
import { FreeTrialBanner } from '@web/components/free-trial-banner'
import { AppRoutes } from '@repo/web-utils/app-routes'

const CF_URL = process.env.NEXT_PUBLIC_CF_URL

interface PageContentProps {
  slug: string
  serverState: InstantSearchServerState
  facetFilters: string
  query: string
  params: string[][]
  page: number
}

function InnerPageContent({
  slug,
  params,
  facetFilters,
  query,
  page,
}: Omit<PageContentProps, 'serverState'>) {
  const locale = useLocale() as Locales
  const t = useTranslations('pages.fonts')
  const title = t(`links.${slug}`)

  const { items, results: { nbHits } = { nbHits: 0 } } = useHits<SearchProduct>()
  const { status, results } = useInstantSearch()

  const pages = Math.ceil(nbHits / HITS_PER_PAGE)

  const onSearch = (value: string) => {
    const newParams = new URLSearchParams([...params, ['query', value]])
    window.location.href = `${AppRoute.SEARCH}?${newParams.toString()}`
  }

  const isLoading = items.length === 0 && (status === 'loading' || results?.__isArtificial)
  const isEmpty = items.length === 0 && status === 'idle' && !results?.__isArtificial

  const urls = [`${AppRoutes.FONTS_RELATIVE_PATH}${slug}/`]

  for (let i = 2; i <= pages; i++) {
    urls.push(`${AppRoutes.FONTS_RELATIVE_PATH}${slug}/page/${i}/`)
  }

  return (
    <div className="flex flex-col gap-12 mb-10">
      <Configure
        hitsPerPage={HITS_PER_PAGE}
        facetFilters={facetFilters}
        query={query}
        page={page - 1}
      />
      <Header count={nbHits} onSearch={onSearch} title={title} />
      <Links slug={slug} location="top" className="w-fit max-w-full mx-auto" />
      <div className="flex flex-col gap-5">
        <div className="flex items-center sm:justify-between justify-center">
          {!isLoading ? (
            <span className="max-sm:hidden">
              {t('stats', {
                page: `${(page - 1) * HITS_PER_PAGE + 1}-${(page - 1) * HITS_PER_PAGE + items.length}`,
                total: nbHits,
              })}
            </span>
          ) : (
            <div className="h-[20px] w-[150px] bg-cf-grey-200 rounded animate-pulse" />
          )}
          <SearchResultsSort />
        </div>

        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-[200px]">
            <Typography variant="cf-title-05">{t('no_results')}</Typography>
          </div>
        ) : null}

        <div
          className="grid grid-flow-row auto-rows-auto grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
          data-testid="product-containers"
        >
          <GoogleItemListJson
            products={items.map((product, index) => ({
              name: product[`name_${locale}`] || product.name_en,
              url: product.url,
              position: index + 1,
            }))}
          />

          {isLoading
            ? Array.from({ length: HITS_PER_PAGE }).map((_, index) => (
                <div key={`search_result_skeleton_${index}`}>
                  <ProductCardSkeleton />
                </div>
              ))
            : null}

          {items
            .filter(product => 'url' in product || 'slug' in product)
            .map((product, index) => {
              const productUrl = product.url?.split('/product/')[1]
              const name = product[`name_${locale}`] || product.name_en

              const countDown =
                product.endDate !== undefined && Boolean(product.endDate)
                  ? product.endDate * 1000
                  : undefined

              return (
                <div key={`search_result_${product.objectID}_${index}`}>
                  <ProductCard
                    productId={String(product.objectID)}
                    productUrl={`/product/${productUrl}`}
                    image={product.image}
                    name={name?.replace(/&nbsp;/gi, ' ') || ''}
                    category={product.type}
                    subcategory={product.category?.[0]}
                    extraSettings={product.extraSettings}
                    countdown={countDown}
                    isLegacy
                    imageClassName="aspect-[3/2] object-cover"
                  />
                </div>
              )
            })}
        </div>

        <div className="flex justify-end">
          {page === 1 ? (
            pages > 1 ? (
              <>
                <link rel="next" href={`${CF_URL}${addOrReplaceLocaleInUrl(urls[1], locale)}`} />
                <LinkComponent
                  className="underline"
                  icon={<IconArrowForward className="size-4" />}
                  href={addOrReplaceLocaleInUrl(urls[1], locale)}
                  data-testid="explore-all-link"
                >
                  {t('explore_all', { total: nbHits, title })}
                </LinkComponent>
              </>
            ) : null
          ) : (
            <Pagination
              className="flex-wrap sm:flex-nowrap"
              urls={urls}
              count={pages}
              page={page}
              testid="fonts-pagination"
            />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-5 text-center">
        <Typography variant="cf-title-06" asChild>
          <h3>{t('related', { title })}</h3>
        </Typography>
        <Links slug={slug} location="bottom" className="justify-center flex-wrap" />
      </div>
      <div className="flex flex-col gap-5 items-center">
        <Typography variant="cf-title-06" asChild>
          <h3>{t('explore_more')}</h3>
        </Typography>
        <Links slug={slug} location="explore-more" />
      </div>
    </div>
  )
}

export function PageContent({ serverState, ...restProps }: PageContentProps) {
  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        searchClient={defaultSearchClient}
        indexName={algoliaProductIndex}
      >
        <span id="enable-bottom-floating-banner" />
        <InnerPageContent {...restProps} />
        <FreeTrialBannerSection>
          <FreeTrialBanner />
        </FreeTrialBannerSection>
      </InstantSearch>
    </InstantSearchSSRProvider>
  )
}
